import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import type { NotificationEvent } from '../../../../../api/notifications/notification.types';

interface UseNotificationCollectionProps {
  notifications: NotificationEvent[];
  onMarkRead?: ((eventId: string) => Promise<void>) | undefined;
  onLoadMore?: (() => void) | undefined;
  hasMore?: boolean | undefined;
  isLoading?: boolean | undefined;
  onOpenConversation?: ((messageId?: string) => void) | undefined;
  open?: boolean | undefined;
}

export function getNotificationDisplay(event: NotificationEvent) {
  switch (event.event_type) {
    case 'message_received':
      return {
        title: 'New message',
        description: event.actor_name
          ? `You have a new message from ${event.actor_name}`
          : 'You have a new message',
      };
    case 'registration_completed':
      return {
        title: 'Registration Complete',
        description: 'Welcome! Your registration is complete.',
      };
    case 'onboarding_completed':
      return {
        title: 'Onboarding Complete',
        description: 'Congratulations! You have completed onboarding.',
      };
    case 'new_first_principles':
      return {
        title: 'New Questions',
        description: 'You have new first principles questions to answer.',
      };
    case 'custom':
      return {
        title: event.subject || 'Notification',
        description: event.actor_name
          ? (event.content ? `${event.actor_name}: ${event.content}` : `From ${event.actor_name}`)
          : (event.content || 'You have a new notification'),
      };
    default:
      return {
        title: 'Notification',
        description: 'You have a new notification',
      };
  }
}

export function formatNotificationTimestamp(createdAt: string): string {
  const date = new Date(createdAt);
  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString(undefined, { month: 'short' });
  const year = String(date.getFullYear()).slice(-2);
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const h12 = hours % 12 || 12;

  return `${day} ${month} ${year} \u00B7 ${h12}:${minutes} ${ampm}`;
}

export const useNotificationCollection = ({
  notifications,
  onMarkRead,
  onLoadMore,
  hasMore,
  isLoading,
  onOpenConversation,
  open,
}: UseNotificationCollectionProps) => {
  const hasNotifications = useMemo(
    () => notifications && notifications.length > 0,
    [notifications]
  );

  const listRef = useRef<HTMLDivElement>(null);

  const handleNotificationClick = useCallback(
    async (event: NotificationEvent) => {
      if (!event.is_read && onMarkRead) {
        await onMarkRead(event.id);
      }

      if (event.event_type === 'message_received') {
        onOpenConversation?.(event.payload?.message_id ?? undefined);
      }
    },
    [onMarkRead, onOpenConversation]
  );

  const checkNeedMore = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore || !onLoadMore || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 80) {
      onLoadMore();
    }
  }, [hasMore, onLoadMore, isLoading]);

  const handleScroll = useCallback(() => {
    checkNeedMore();
  }, [checkNeedMore]);

  // After a page of items is appended, re-check if the user is still near
  // the bottom — if so, load the next page automatically.
  useEffect(() => {
    if (!hasMore || isLoading) return;
    // Small delay to let the DOM settle after new items render
    const id = requestAnimationFrame(() => checkNeedMore());
    return () => cancelAnimationFrame(id);
  }, [notifications.length, hasMore, isLoading, checkNeedMore]);

  /* ─── Visual unread: keep unread styling for 2.5s after panel opens ─── */
  const [visuallyUnreadIds, setVisuallyUnreadIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      setVisuallyUnreadIds(new Set());
      return;
    }
    const unreadIds = new Set(
      notifications.filter((n) => !n.is_read).map((n) => n.id),
    );
    setVisuallyUnreadIds(unreadIds);
    if (unreadIds.size === 0) return;

    const timer = setTimeout(() => setVisuallyUnreadIds(new Set()), 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ─── IntersectionObserver: mark unread notifications as read when visible ─── */
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Track which IDs we've already fired markRead for to avoid duplicate calls
  const markedRef = useRef<Set<string>>(new Set());

  // Stable ref for onMarkRead so the observer effect only depends on `open`
  // (markRead changes identity on every fetch due to its dependency chain,
  // which would destroy/recreate the observer on every pagination load).
  const onMarkReadRef = useRef(onMarkRead);
  onMarkReadRef.current = onMarkRead;

  useEffect(() => {
    // Reset marked set when panel closes so re-opening can re-observe
    if (!open) {
      markedRef.current.clear();
      return;
    }
    if (!onMarkReadRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const markFn = onMarkReadRef.current;
        if (!markFn) return;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          const eventId = el.dataset.notifId;
          const isUnread = el.dataset.notifUnread === 'true';
          if (eventId && isUnread && !markedRef.current.has(eventId)) {
            markedRef.current.add(eventId);
            markFn(eventId);
          }
        }
      },
      {
        root: listRef.current,
        // Notification counts as "seen" once 50% visible for a moment
        threshold: 0.5,
      },
    );

    observerRef.current = observer;

    // Observe any rows already rendered before this effect ran (fixes
    // race where ref callbacks fire during render before the observer
    // is created in this post-render effect).
    if (listRef.current) {
      const rows = listRef.current.querySelectorAll<HTMLElement>('[data-notif-unread="true"]');
      rows.forEach((row) => {
        const eventId = row.dataset.notifId;
        if (eventId && !markedRef.current.has(eventId)) {
          observer.observe(row);
        }
      });
    }

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [open]);

  // Callback ref for each notification row — always sets data attributes
  // so the observer can pick them up even if it's created after the render.
  const observeRow = useCallback((node: HTMLElement | null, eventId: string, isUnread: boolean) => {
    if (!node || !isUnread || markedRef.current.has(eventId)) return;

    // Always stamp data attributes so the useEffect above can query them
    node.dataset.notifId = eventId;
    node.dataset.notifUnread = String(isUnread);

    // If observer is already up, observe immediately (pagination / re-render)
    const obs = observerRef.current;
    if (obs) {
      obs.observe(node);
    }
  }, []);

  return {
    hasNotifications,
    handleNotificationClick,
    handleScroll,
    listRef,
    observeRow,
    visuallyUnreadIds,
  };
};
